<template>
  <div class="w-full lg:w-3/5">
    <div
      v-for="(occupancy, index) in normalizedOccupancy"
      :key="index"
      class="relative mb-4 overflow-hidden rounded-lg border"
    >
      <div class="p-4">
        <h3 class="text-lg font-bold">
          {{ normalizedRoomName }} {{ normalizedOccupancy.length > 1 ? index + 1 : '' }}
        </h3>
        <h5 class="mb-4">
          {{ extractAdults(occupancy) }} {{ extractAdults(occupancy) === 1 ? 'adult' : 'adults' }}
          {{ extractChildren(occupancy) }} {{ extractChildren(occupancy) === 1 ? 'child' : 'children' }}
          {{ extractInfants(occupancy) }} {{ extractInfants(occupancy) === 1 ? 'infant' : 'infants' }}
        </h5>
        <div class="mb-3 grid gap-4 md:grid-cols-2">
          <div class="flex flex-col gap-2">
            <label :for="`given_name_${index}`" class="required"
              >Given name <span class="text-red-500">*</span></label
            >
            <input
              type="text"
              maxlength="50"
              class="w-full rounded border border-stone-400 p-2"
              :name="`given_name_${index}`"
              v-model="userGivenNames[index]"
              required
            />
            <div
              class="error error-message text-red-500 text-sm"
              :id="`rooms_${index}_given_name-error`"
              v-if="validationErrors[`userGivenNames.${index}`]"
            >
              {{ validationErrors[`userGivenNames.${index}`] }}
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <label :for="`family_name_${index}`" class="required"
              >Family name <span class="text-red-500">*</span></label
            >
            <input
              type="text"
              maxlength="50"
              class="w-full rounded border border-stone-400 p-2"
              :name="`family_name_${index}`"
              v-model="userFamilyNames[index]"
              required
            />
            <div
              class="error error-message text-red-500 text-sm"
              :id="`rooms_${index}_family_name-error`"
              v-if="validationErrors[`userFamilyNames.${index}`]"
            >
              {{ validationErrors[`userFamilyNames.${index}`] }}
            </div>
          </div>
          <div class="col-span-2 flex flex-col gap-2">
            <label :for="`phone_${index}`" class="required"
              >Mobile phone number <span class="text-red-500">*</span></label
            >
            <div class="flex w-full">
              <select
                class="border border-stone-400 rounded-l w-1/3"
                :value="selectedPhoneCountries[index]"
                @input="updatePhoneCountry(index, $event.target.value)"
              >
                <option value="">Select a country</option>
                <option
                  v-for="country in countries"
                  :key="country.code"
                  :value="country.code"
                >
                  {{ country.name }}(+{{ country.code }})
                </option>
              </select>
              <input
                type="tel"
                :id="`phone_${index}`"
                :name="`phone_${index}`"
                class="w-3/4 rounded-r border border-stone-400 p-2"
                :value="userPhones[index]"
                @input="updatePhone(index, $event.target.value)"
              />
            </div>
            <div
              class="error error-message text-red-500 text-sm"
              v-if="validationErrors[`userPhones.${index}`]"
            >
              {{ validationErrors[`userPhones.${index}`] }}
            </div>
          </div>
        </div>
        <div>
          <p
            @click="toggleSpecialRequest(index)"
            class="text-sm underline cursor-pointer"
          >
            Special Requests (optional)
          </p>
          <div>
            <div
              v-if="showSpecialRequests[index]"
              class="mt-2 rounded bg-stone-100 p-2 shadow-inner"
            >
              <p class="mb-2 text-sm">
                Special/accessibility requests (e.g. roll-away beds, late
                check-in, and accessible rooms) are not guaranteed. If you don't
                hear back from the property, you may want to contact them
                directly to confirm. The property may charge a fee for certain
                special requests.
              </p>
              <textarea
                :name="`special_request_${index}`"
                maxlength="250"
                placeholder="Limit 250 characters"
                class="w-full rounded border border-stone-400 p-2"
                v-model="userSpecialRequests[index]"
              ></textarea>
            </div>
          </div>
        </div>
        <p
          class="mt-2 border-t border-stone-200 pt-2 text-xs italic text-stone-500"
        >
          Choices between multiple bed types for the same room are requests only
          and may not be honored at the hotel if availability does not permit
        </p>
      </div>
    </div>

    <div class="relative mb-4 overflow-hidden rounded-lg border">
      <div class="p-4">
        <strong class="mb-2 font-bold">Address</strong>
        <div class="grid gap-4 md:grid-cols-3">
          <div class="flex flex-col gap-2">
            <label for="addressLine1" class="required"
              >Line 1 <span class="text-red-500">*</span></label
            >
            <input
              type="text"
              class="w-full rounded border border-stone-400 p-2"
              maxlength="200"
              id="addressLine1"
              name="addressLine1"
              v-model="userAddressLine1"
              required
            />
            <div
              class="error error-message text-red-500 text-sm"
              v-if="validationErrors.userAddressLine1"
            >
              {{ validationErrors.userAddressLine1 }}
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <label for="addressLine2">Line 2</label>
            <input
              type="text"
              class="w-full rounded border border-stone-400 p-2"
              maxlength="200"
              id="addressLine2"
              name="addressLine2"
              v-model="userAddressLine2"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label for="addressLine3">Line 3</label>
            <input
              type="text"
              class="w-full rounded border border-stone-400 p-2"
              maxlength="200"
              id="addressLine3"
              name="addressLine3"
              v-model="userAddressLine3"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label for="city" class="required"
              >City <span class="text-red-500">*</span></label
            >
            <input
              type="text"
              class="w-full rounded border border-stone-400 p-2"
              maxlength="200"
              id="city"
              name="city"
              v-model="userCity"
              required
            />
            <div
              class="error error-message text-red-500 text-sm"
              v-if="validationErrors.userCity"
            >
              {{ validationErrors.userCity }}
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <label for="state_province_code" class="required"
              >State <span class="text-red-500">*</span></label
            >
            <select
              name="state_province_code"
              class="w-full rounded border border-stone-400 p-2"
              v-model="userState"
            >
              <option value="">Select a state</option>
              <option
                v-for="(name, code)  in bookedRoom.states"
                :key="code"
                :value="code"
              >
                {{ name }}
              </option>
            </select>
            <div
              class="error error-message text-red-500 text-sm"
              v-if="validationErrors.userState"
            >
              {{ validationErrors.userState }}
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <label for="postal_code" class="required"
              >Postal Code <span class="text-red-500">*</span></label
            >
            <input
              type="text"
              class="w-full rounded border border-stone-400 p-2"
              maxlength="10"
              id="postal_code"
              name="postal_code"
              v-model="userPostalCode"
              required
            />
            <div
              class="error error-message text-red-500 text-sm"
              v-if="validationErrors.userPostalCode"
            >
              {{ validationErrors.userPostalCode }}
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <label for="country_code" class="required"
              >Country <span class="text-red-500">*</span></label
            >
            <select
              class="w-full rounded border border-stone-400 p-2"
              v-model="selectedCountry"
            >
              <option value="">Select a country</option>
              <option
                v-for="(name, code)  in bookedRoom.countries"
                :key="code"
                :value="code"
              >
              {{ name }}
              </option>
            </select>
            <div
              class="error error-message text-red-500 text-sm"
              v-if="validationErrors.selectedCountry"
            >
              {{ validationErrors.selectedCountry }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="relative mb-4 overflow-hidden rounded-lg border">
      <div class="p-4">
        <strong class="mb-4 font-bold">Manage your booking</strong>
        <div class="mb-4 text-sm text-stone-600">
          <strong class="mb-1 font-bold">Confirmation email</strong>
          <p>
            Please enter the email address where you would like to receive your
            confirmation.
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <label for="email" class="required"
            >Email address <span class="text-red-500">*</span></label
          >
          <input
            type="email"
            class="w-full rounded border border-stone-400 p-2"
            id="email"
            name="email"
            v-model="userEmail"
            required
          />
          <div
            class="error error-message text-red-500 text-sm"
            v-if="validationErrors.userEmail"
          >
            {{ validationErrors.userEmail }}
          </div>
        </div>
      </div>
    </div>

    <!-- R2B Extras Section -->
    <div 
      v-if="bookedRoom?.thirdPartyType === 'R2B' && r2bExtras.length > 0"
      class="relative mb-4 overflow-hidden rounded-lg border"
    >
      <div class="p-4">
        <strong class="mb-4 font-bold block">Extras</strong>
        <div class="space-y-3">
          <div 
            v-for="(extra, index) in r2bExtras" 
            :key="index"
            class="pb-3 border-b last:border-b-0"
          >
            <!-- Optional Extras with Checkbox -->
            <div v-if="extra.type === '0' || extra.type === 0" class="flex items-start gap-3">
              <input 
                type="checkbox" 
                :id="`extra-${index}`"
                :value="index"
                v-model="selectedLocalExtras"
                @change="onExtrasChange"
                :disabled="loadSplitCard"
                class="mt-1 w-4 h-4"
                :class="{ 'opacity-50 cursor-not-allowed': loadSplitCard }"
              />
              <label 
                :for="`extra-${index}`" 
                class="flex-1 text-sm"
                :class="loadSplitCard ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'"
              >
                <div class="font-medium mb-1">{{ extra.extra_name }}</div>
                <div class="text-gray-600 space-y-0.5">
                  <div>Charge Type: {{ formatChargeType(extra.charge_type) }}</div>
                  <div>Cost: AUD ${{ extra.cost }} <span class="text-xs">(inc. GST)</span></div>
                  <div class="text-gray-600 font-medium">Type: Optional</div>
                </div>
              </label>
            </div>

            <!-- Compulsory Extras without Checkbox -->
            <div v-else class="text-sm" :class="{ 'opacity-50': loadSplitCard }">
              <div class="font-medium mb-1">{{ extra.extra_name }}</div>
              <div class="text-gray-600 space-y-0.5">
                <div>Charge Type: {{ formatChargeType(extra.charge_type) }}</div>
                <div>Cost: AUD ${{ extra.cost }} <span class="text-xs">(inc. GST)</span></div>
                <div class="text-gray-600 font-medium">Type: Compulsory</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="relative mb-4 overflow-hidden rounded-lg border border-stone-300 shadow-sm"
    >
      <div class="p-4">
        <strong class="mb-2 border-b border-stone-200 py-2 font-bold">
          Price details
        </strong>
        <div class="grid grid-cols-2 gap-2">
          <div>Sub-Total</div>
          <div class="text-right">
            AUD ${{ formatPrice(updatedSubtotal) }}
          </div>

          <div>Taxes</div>
          <div class="text-right">
            AUD ${{ formatPrice(updatedTaxes) }}
          </div>

          <div class="text-lg font-bold">Total</div>
          <div class="text-right text-lg font-bold">
            AUD ${{ formatPrice(updatedTotal) }}
          </div>
        </div>
        <p
          class="text-xs text-stone-600 mt-4"
          third-party-type="Ratehawk"
          terms_route="terms_and_conditions"
        >
          This charge includes estimated amounts the travel service provider
          (i.e. hotel, car rental company) pays for their taxes, /or taxes that
          we pay, to taxing authorities on your booking (including but not
          limited to sales, occupancy, and value added tax). This amount may
          also include any amounts charged to us for resort fees, cleaning fees,
          and other fees and/or a fee we, the hotel supplier and/or the site you
          booked on, retain as part of the compensation for our and/or their
          services, which varies based on factors such as location, the amount,
          and how you booked. For more details, please see the
          <a
            :href="termsAndConditionUrl"
            class="text-blue-900 underline"
            target="_blank"
          >
            Terms and Conditions</a
          >.
        </p>
      </div>
    </div>
    <input type="hidden" value="p-2e6f37ce-f86c-44e6-902f-6a0e1a26c1bd" />

    <div
      v-if="!loadSplitCard"
      class="relative mb-4 overflow-hidden rounded-lg border border-stone-300 p-4 shadow-sm"
    >
      <div class="float-right flex items-center">
        <span class="pr-4 text-sm">Click the button to make a payment: </span>
        <button
          type="button"
          class="lsp-btn mt-3 px-3 py-2 rounded text-sm font-medium text-center"
          @click="validateAndProceed"
        >
          Proceed To Payment
        </button>
      </div>
    </div>
    <div v-if="loadSplitCard">
      <splitCard
        :price="updatedTotal"
        :firstName="userGivenNames[0]"
        :email="userEmail"
        :bookingType="bookedRoom.thirdPartyType || bookedRoom.listing?.third_party_type || bookedRoom.third_party_type || 'Ratehawk'"
        @payment-success="handlePaymentSuccess"
        @payment-error="handlePaymentError"
        @payment-cancelled="handlePaymentCancelled"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive } from "vue";
import { countryList } from "../../utils/countryList";
import { API_URL } from "../../src/constants";
import splitCard from "../../src/UI/splitCard.vue";
import api from "../../utils/api";
import { useBookingStore } from "../../stores/booking.module";
import { useComponentChangeStore } from "../../stores/componentChange.module.js";
import { formatPrice } from "../../utils/imageFunction.js";

const props = defineProps({
  bookedRoom: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['booking-error']);

const countries = countryList;
const showSpecialRequest = ref(false);
const componentChangeStore = useComponentChangeStore();
const bookingStore = useBookingStore();

// Create a normalized occupancy array for both regular and R2B bookings
const normalizedOccupancy = computed(() => {
  // Check if it's an R2B booking (has thirdPartyType === 'R2B')
  if (props.bookedRoom?.thirdPartyType === 'R2B') {
    // For R2B, create a single occupancy entry with adults, children, infants
    return [{
      adults: parseInt(props.bookedRoom?.adults) || 0,
      children: parseInt(props.bookedRoom?.children) || 0,
      infants: parseInt(props.bookedRoom?.infants) || 0
    }];
  }
  // For regular bookings, use the existing occupancy array
  return props.bookedRoom?.occupancy || [];
});

// Get room name for both regular and R2B bookings
const normalizedRoomName = computed(() => {
  // For R2B bookings
  if (props.bookedRoom?.thirdPartyType === 'R2B') {
    return props.bookedRoom?.listing?.listing_name || props.bookedRoom?.content?.name || 'Standard room';
  }
  // For regular bookings
  return props.bookedRoom?.room?.room_name || 'Standard room';
});

const userGivenNames = ref(normalizedOccupancy.value?.map(() => "") || []);
const userFamilyNames = ref(normalizedOccupancy.value?.map(() => "") || []);
const showSpecialRequests = ref(
  normalizedOccupancy.value?.map(() => false) || []
);
const userSpecialRequests = ref(
  normalizedOccupancy.value?.map(() => "") || []
);

// Room-specific phone and country details
const selectedPhoneCountries = ref(
  normalizedOccupancy.value?.map(() => "61") || []
);
const userPhones = ref(normalizedOccupancy.value?.map(() => "") || []);
const payload = ref(null);
const userAddressLine1 = ref("");
const userAddressLine2 = ref("");
const userAddressLine3 = ref("");
const userCity = ref("");
const userState = ref("");
const userPostalCode = ref("");
const selectedCountry = ref("AU");
const userEmail = ref("");
const loadSplitCard = ref(false);
const paymentStatus = ref(null);
const paymentTransactionId = ref(null);

// R2B Extras tracking
const selectedLocalExtras = ref([]);
const calculatedPriceData = ref(null);

// Get R2B extras
const r2bExtras = computed(() => {
  return props.bookedRoom?.listing?.r2b_property_details?.extras || [];
});

// Format charge type for display
const formatChargeType = (chargeType) => {
  if (chargeType === 'per_night') return 'Per Night';
  if (chargeType === 'per_stay') return 'Per Stay';
  return chargeType;
};

// Handle extra selection change
const onExtrasChange = async () => {
  // Store selected extras in localStorage
  const selected = selectedLocalExtras.value.map(index => r2bExtras.value[index]);
  localStorage.setItem('selectedR2BExtras', JSON.stringify(selected));
  
  // If no extras selected, reset to original values
  if (selectedLocalExtras.value.length === 0) {
    calculatedPriceData.value = null;
    return;
  }
  
  // Call API to get calculated price with extras
  try {
    const selectedExtraNames = selectedLocalExtras.value.map(
      index => r2bExtras.value[index].extra_name
    );
    
    const payload = {
      check_in: props.bookedRoom.checkin,
      check_out: props.bookedRoom.checkout,
      extra_name: selectedExtraNames,
      listing_id: parseInt(props.bookedRoom.listing_id),
      listing_plus_extras_compolsory_total: parseFloat(
        props.bookedRoom.listing_plus_extras_compolsory_total || 
        props.bookedRoom.totals?.listing_plus_extras_compolsory_total || 0
      ),
      only_room_price: parseFloat(
        props.bookedRoom.only_room_price || 
        props.bookedRoom.totals?.only_room_price || 0
      )
    };
    
    const response = await api.post('/property/get-r2b-extras-calculated-price', payload);
    
    if (response && response.data && response.data.data) {
      // Store the API response data
      calculatedPriceData.value = response.data.data;
    }
  } catch (error) {
    console.error('Error calculating extras price:', error);
    // Reset to original values on error
    calculatedPriceData.value = null;
  }
};

// Get number of nights for calculations
const getNightsStay = computed(() => {
  if (!props.bookedRoom?.checkin || !props.bookedRoom?.checkout) {
    return 1;
  }
  
  const checkin = new Date(props.bookedRoom.checkin);
  const checkout = new Date(props.bookedRoom.checkout);
  const diffTime = Math.abs(checkout - checkin);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays || 1;
});

const calculateExtrasCostExcGST = computed(() => {
  if (!selectedLocalExtras.value || selectedLocalExtras.value.length === 0) {
    return 0;
  }
  
  const nights = getNightsStay.value;
  let totalExtrasCost = 0;
  
  selectedLocalExtras.value.forEach(index => {
    const extra = r2bExtras.value[index];
    if (extra) {
      const costExcGst = parseFloat(extra.cost_exc_gst) || 0;
      // If per_night, multiply by number of nights
      if (extra.charge_type === 'per_night') {
        totalExtrasCost += costExcGst * nights;
      } else {
        // per_stay - charge once
        totalExtrasCost += costExcGst;
      }
    }
  });
  
  return totalExtrasCost;
});

const calculateExtrasGST = computed(() => {
  if (!selectedLocalExtras.value || selectedLocalExtras.value.length === 0) {
    return 0;
  }
  
  const nights = getNightsStay.value;
  let totalExtrasGST = 0;
  
  selectedLocalExtras.value.forEach(index => {
    const extra = r2bExtras.value[index];
    if (extra) {
      const cost = parseFloat(extra.cost) || 0;
      const costExcGst = parseFloat(extra.cost_exc_gst) || 0;
      const gst = cost - costExcGst;
      if (extra.charge_type === 'per_night') {
        totalExtrasGST += gst * nights;
      } else {
        totalExtrasGST += gst;
      }
    }
  });
  
  return totalExtrasGST;
});

// Calculate compulsory extras cost (always included)
const calculateCompulsoryExtrasCost = computed(() => {
  if (!r2bExtras.value || r2bExtras.value.length === 0) {
    return 0;
  }
  
  const nights = getNightsStay.value;
  let totalCompulsoryCost = 0;
  
  r2bExtras.value.forEach(extra => {
    // Only include compulsory extras (type === '1' or type === 1)
    if (extra.type === '1' || extra.type === 1) {
      const cost = parseFloat(extra.cost) || 0;
      if (extra.charge_type === 'per_night') {
        totalCompulsoryCost += cost * nights;
      } else {
        totalCompulsoryCost += cost;
      }
    }
  });
  
  return totalCompulsoryCost;
});
const updatedSubtotal = computed(() => {
  // Use API response if available, otherwise calculate locally
  if (calculatedPriceData.value?.raw_data?.sub_total !== undefined) {
    return parseFloat(calculatedPriceData.value.raw_data.sub_total) || 0;
  }
  const originalSubtotal = parseFloat(props.bookedRoom?.totals?.subtotal) || 0;
  const optionalExtrasExcGST = calculateExtrasCostExcGST.value; // Only add optional extras exc GST
  return originalSubtotal + optionalExtrasExcGST;
});

const updatedTaxes = computed(() => {
  // Use API response if available, otherwise calculate locally
  if (calculatedPriceData.value?.raw_data?.gst_amount !== undefined) {
    return parseFloat(calculatedPriceData.value.raw_data.gst_amount) || 0;
  }
  const originalTaxes = parseFloat(props.bookedRoom?.totals?.gst_amount) || 0;
  const extrasGST = calculateExtrasGST.value;
  
  return originalTaxes + extrasGST;
});

// Calculate updated total
const updatedTotal = computed(() => {
  // Use API response if available, otherwise calculate locally
  if (calculatedPriceData.value?.raw_data?.total_amount !== undefined) {
    return parseFloat(calculatedPriceData.value.raw_data.total_amount) || 0;
  }
  return updatedSubtotal.value + updatedTaxes.value;
});

// Validation errors
const validationErrors = reactive({});

const termsAndConditionUrl = computed(() => {
  return `${API_URL}/terms_and_conditions`;
});

// Validation function
const validateAndProceed = () => {
  // Clear previous errors
  Object.keys(validationErrors).forEach((key) => {
    delete validationErrors[key];
  });

  let isValid = true;

  // Validate room-specific names
  normalizedOccupancy.value?.forEach((_, index) => {
    if (!userGivenNames.value[index]?.trim()) {
      validationErrors[`userGivenNames.${index}`] = `Given name for Room ${
        index + 1
      } is required`;
      isValid = false;
    }

    if (!userFamilyNames.value[index]?.trim()) {
      validationErrors[`userFamilyNames.${index}`] = `Family name for Room ${
        index + 1
      } is required`;
      isValid = false;
    }

    // Validate phone for each room
    if (!selectedPhoneCountries.value[index]) {
      validationErrors[`userPhones.${index}`] = `Phone country for Room ${
        index + 1
      } is required`;
      isValid = false;
    }

    if (!userPhones.value[index]) {
      validationErrors[`userPhones.${index}`] = `Mobile phone number for Room ${
        index + 1
      } is required`;
      isValid = false;
    }
  });

  // Validate other fields (address, email, etc.) as in the original code
  if (!userAddressLine1.value.trim()) {
    validationErrors.userAddressLine1 = "Address line 1 is required";
    isValid = false;
  }

  if (!userCity.value.trim()) {
    validationErrors.userCity = "City is required";
    isValid = false;
  }

  if (!userState.value) {
    validationErrors.userState = "State is required";
    isValid = false;
  }

  if (!userPostalCode.value) {
    validationErrors.userPostalCode = "Postal code is required";
    isValid = false;
  }

  if (!selectedCountry.value) {
    validationErrors.selectedCountry = "Country is required";
    isValid = false;
  }

  if (!userEmail.value.trim()) {
    validationErrors.userEmail = "Email address is required";
    isValid = false;
  } else if (!isValidEmail(userEmail.value)) {
    validationErrors.userEmail = "Please enter a valid email address";
    isValid = false;
  }

  if (isValid) {
    loadSplitCard.value = true;
    const formData = new FormData();

    normalizedOccupancy.value?.forEach((occupancy, index) => {
      formData.append(
        `rooms[${index}][given_name]`,
        userGivenNames.value[index]
      );
      formData.append(
        `rooms[${index}][family_name]`,
        userFamilyNames.value[index]
      );
      formData.append(
        `rooms[${index}][special_request]`,
        userSpecialRequests.value[index] || null
      );
    });

    formData.append("country_code_phone", selectedPhoneCountries.value[0]);
    formData.append("phone", userPhones.value[0]);

    formData.append("line_1", userAddressLine1.value);
    formData.append("line_2", userAddressLine2.value);
    formData.append("line_3", userAddressLine3.value);
    formData.append("city", userCity.value);
    formData.append("state_province_code", userState.value);
    formData.append("postal_code", userPostalCode.value);
    formData.append("country_code", selectedCountry.value);
    formData.append("email", userEmail.value);
    formData.append("book_hash", props.bookedRoom.book_hash);

    if (props.bookedRoom.content?.cancellation_policy) {
      formData.append(
        "cancellation_policy",
        JSON.stringify(props.bookedRoom.content.cancellation_policy)
      );
    }

    formData.append("room_id", props.bookedRoom.room_id);
    formData.append("listing_id", props.bookedRoom.listing_id);
    formData.append("property_id", props.bookedRoom.property_id);
    formData.append("checkin", props.bookedRoom.checkin);
    formData.append("checkout", props.bookedRoom.checkout);
    
    const finalAmount = calculateExtrasCostExcGST.value > 0 
      ? updatedTotal.value 
      : props.bookedRoom.totals.total;
    formData.append("rate_id", finalAmount);

    if (selectedLocalExtras.value && selectedLocalExtras.value.length > 0) {
      const selected = selectedLocalExtras.value.map(index => r2bExtras.value[index]);
      formData.append("selected_extras", JSON.stringify(selected));
    }

    payload.value = formData
  }
};

const handlePaymentSuccess = (data) => {
  paymentStatus.value = "success";
  paymentTransactionId.value = data.transactionId;
  
  if (payload.value) {
    bookingStore.setLoading(true);
    
    const isR2BBooking = props.bookedRoom?.thirdPartyType === 'R2B' || 
                         props.bookedRoom?.third_party_type === 'REQUESTTOBOOK';
    
    if (isR2BBooking) {
      const compulsoryExtras = r2bExtras.value
        .filter(extra => extra.type === '1' || extra.type === 1)
        .map(extra => extra.extra_name);
      
      const selectedOptionalExtras = selectedLocalExtras.value
        .map(index => r2bExtras.value[index].extra_name);
      
      const allExtras = [...compulsoryExtras, ...selectedOptionalExtras];
      
      const r2bPayload = {
        listing_id: parseInt(props.bookedRoom.listing_id),
        checkin: props.bookedRoom.checkin,
        checkout: props.bookedRoom.checkout,
        adults: parseInt(props.bookedRoom.adults || 0),
        children: parseInt(props.bookedRoom.children || 0),
        infants: parseInt(props.bookedRoom.infants || 0),
        given_name: userGivenNames.value[0],
        family_name: userFamilyNames.value[0],
        country_code_phone: selectedPhoneCountries.value[0],
        phone: userPhones.value[0],
        special_request: userSpecialRequests.value[0] || null,
        line_1: userAddressLine1.value,
        line_2: userAddressLine2.value || null,
        line_3: userAddressLine3.value || null,
        city: userCity.value,
        state_province_code: userState.value,
        postal_code: String(userPostalCode.value),
        country_code: selectedCountry.value,
        email: userEmail.value,
        extras: allExtras,
        total: JSON.stringify({
          actual_amount: calculatedPriceData.value?.raw_data?.actual_amount !== undefined
            ? parseFloat(calculatedPriceData.value.raw_data.actual_amount)
            : parseFloat(props.bookedRoom.totals?.actual_amount || 0),
          commission_amount: calculatedPriceData.value?.raw_data?.commission_amount !== undefined
            ? parseFloat(calculatedPriceData.value.raw_data.commission_amount)
            : parseFloat(props.bookedRoom.totals?.commission_amount || 0),
          gst_amount: updatedTaxes.value,
          gst_plus_commission: calculatedPriceData.value?.raw_data?.gst_plus_commission !== undefined
            ? parseFloat(calculatedPriceData.value.raw_data.gst_plus_commission)
            : parseFloat(props.bookedRoom.totals?.gst_plus_commission || 0),
          sub_total: updatedSubtotal.value,
          total_amount: updatedTotal.value,
          only_room_price: calculatedPriceData.value?.raw_data?.only_room_price !== undefined
            ? parseFloat(calculatedPriceData.value.raw_data.only_room_price)
            : parseFloat(props.bookedRoom.totals?.only_room_price || 0),
        }),
        cancellation_policy: props.bookedRoom.content?.cancellation_policy || '',
        payment_type: 'card_payment',
        payment_obj: JSON.stringify(data),
      };
      
      api.post("/property/r2b-booking", r2bPayload)
        .then((response) => {
          if (response) {
            const responseData = response.data.data;
            const bookingData = {
              ...responseData.booking,
              booking_id: responseData.booking_id
            };
            bookingStore.setConfirmBookinDetails(bookingData);
            componentChangeStore.changeTheComponent("BookingConfirmationView");
            localStorage.setItem("componentLoad", "BookingConfirmationView");
            localStorage.setItem("confirmBookingDetails", JSON.stringify(bookingData));
          }
        })
        .catch((err) => {
          emit('booking-error', err.response?.data?.message || 'Booking failed. Please try again.');
        })
        .finally(() => {
          bookingStore.setLoading(false);
        });
    } else {
      payload.value.append("payment_object", JSON.stringify(data));
      
    const payloadObject = {};
    for (const [key, value] of payload.value.entries()) {
      payloadObject[key] = value;
    }
    
    api.post("/property/order-booking", payload.value)
      .then((response) => {
        if (response){
          bookingStore.setConfirmBookinDetails(response.data.data);
          componentChangeStore.changeTheComponent("BookingConfirmationView");
          localStorage.setItem("componentLoad", "BookingConfirmationView");
          localStorage.setItem("confirmBookingDetails", JSON.stringify(response.data.data));
        }
      })
      .catch((err) => {
        emit('booking-error', err.response?.data?.message || 'Booking failed. Please try again.');
        })
        .finally(() => {
          bookingStore.setLoading(false);
        });
    }
  }
  
  loadSplitCard.value = false;
};

const handlePaymentError = (data) => {
  paymentStatus.value = "failed";
  loadSplitCard.value = false;
};

const handlePaymentCancelled = () => {
  paymentStatus.value = "cancelled";
  loadSplitCard.value = false;
};

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const toggleSpecialRequest = (index) => {
  showSpecialRequests.value[index] = !showSpecialRequests.value[index];
};

const updatePhoneCountry = (index, value) => {
  selectedPhoneCountries.value[index] = value;
};

// Update phone number for a specific room
const updatePhone = (index, value) => {
  userPhones.value[index] = value;
};

const extractAdults = (occupancy) => {
  if (typeof occupancy === 'object' && occupancy.adults !== undefined) {
    return occupancy.adults;
  }
  if (typeof occupancy === 'string') {
    return occupancy.split("-")[0];
  }
  return 0;
};

const extractChildren = (occupancy) => {
  if (typeof occupancy === 'object' && occupancy.children !== undefined) {
    return occupancy.children;
  }
  if (typeof occupancy === 'string') {
    return occupancy.split("-")[1];
  }
  return 0;
};

const extractInfants = (occupancy) => {
  if (typeof occupancy === 'object' && occupancy.infants !== undefined) {
    return occupancy.infants;
  }
  if (typeof occupancy === 'string') {
    return occupancy.split("-")[2];
  }
  return 0;
};
</script>
<style scoped>
.payment-section {
  margin-top: 20px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f9f9f9;
}
</style>
